import { useEffect, useState } from 'react';
import { Link, Text } from '@nimbus-ds/components';
import { Trans } from 'react-i18next';
import { IRecipient } from '@tiendanube/common';
import { Country } from '@tiendanube/common/src/enums';
import { NUVEMPAGO_ENABLED } from 'App/featuresFlags';
import { useHasTags, useNavegate } from 'App/hooks';
import { useToastStatus } from 'commons/hooks';
import { useGetCountry } from 'domains/Auth/hooks';
import dashboardService from 'domains/Dashboard/services';
import {
  useGetRecipient,
  useUpdateRecipient,
} from 'domains/PagoNubeOnboarding/Recipient/hooks';
import { mapPersonalDataToRecipient } from 'domains/PagoNubeOnboarding/Recipient/utils';
import useTranslationPagoNubeOnboarding from 'domains/PagoNubeOnboarding/useTranslationPagoNubeOnboarding';
import { useStoreInfo } from 'domains/PartnersApps/hooks';
import { PAYMENTS_ROUTES } from 'domains/Payments/paymentsRoutes';
import { PersonalData } from './PersonalData';
import { PlaceOfResidenceData } from './PlaceofResidenceData';
import { SaveAndContinueButtons } from './SaveAndContinueButtons';
import { useFormValidation } from '../../hooks/useFormValidation';

function IndividualForm() {
  const t = useTranslationPagoNubeOnboarding('individualForm');

  const storeCountry = useGetCountry();
  const [isNuvemPagoMx] = useHasTags([NUVEMPAGO_ENABLED]);
  const isMx = Country.MX === storeCountry;

  const [isSaveAndContinueBtnClicked, setIsSaveAndContinueBtnClicked] =
    useState(false);

  const { id: storeId } = useStoreInfo();
  const { goTo } = useNavegate();

  const { fetchRecipient } = useGetRecipient();
  const {
    isError,
    isSuccess,
    resetUpdateRecipientData,
    updateRecipient,
    updateRecipientData,
  } = useUpdateRecipient();

  useEffect(() => {
    if (!isSuccess) return;
    if (
      updateRecipientData?.accounts?.[0]?.status === 'ACTIVE' ||
      isSaveAndContinueBtnClicked
    ) {
      goTo(PAYMENTS_ROUTES.PAYMENTS_BASE_ROUTE);
    } else {
      fetchRecipient(storeId, true);
    }
  }, [
    updateRecipientData,
    isSuccess,
    fetchRecipient,
    goTo,
    isSaveAndContinueBtnClicked,
    storeId,
  ]);

  useEffect(
    () => () => {
      resetUpdateRecipientData();
    },
    [resetUpdateRecipientData],
  );

  useToastStatus({
    status: isError ? 'error' : 'idle',
    error: t('errorMsg'),
  });

  const handleSubmit = async () => {
    if (!haveErrors() && !isAddressesMexicoError) {
      if (isMx && isNuvemPagoMx) {
        await dashboardService.updateOnboardingTaskSetupPayments();
      }
      const recipient: IRecipient =
        mapPersonalDataToRecipient(personalDataState);
      updateRecipient(storeId, recipient);
    }
  };

  const {
    errors,
    handleOnBlur,
    handleChangeAddressesData,
    handleChangeStreetNumber,
    handleOnChange,
    handleOnSubmit,
    isAddressesMexicoError,
    isAddressesMexicoLoading,
    isWithoutStreetNumberChecked,
    values: personalDataState,
  } = useFormValidation(handleSubmit);

  const haveErrors = () => Object.values(errors).some((error) => !!error);

  return (
    <>
      <PersonalData
        errors={errors}
        handleOnBlur={handleOnBlur}
        onChange={handleOnChange}
        personalDataState={personalDataState}
      />
      <PlaceOfResidenceData
        errors={errors}
        handleOnBlur={handleOnBlur}
        handleChangeAddressesData={handleChangeAddressesData}
        handleChangeStreetNumber={handleChangeStreetNumber}
        isLoading={isAddressesMexicoLoading}
        isError={isAddressesMexicoError}
        isWithoutStreetNumberChecked={isWithoutStreetNumberChecked}
        personalDataState={personalDataState}
      />
      <Text>
        <Trans
          t={t}
          i18nKey="termsAndConditions"
          components={{
            termsAndConditionsLink: (
              <Link
                as="a"
                appearance="primary"
                href={t('termsAndConditionsLink')}
                target="_blank"
                textDecoration="underline"
              >
                {' '}
              </Link>
            ),
            stripeAgreementLink: (
              <Link
                as="a"
                appearance="primary"
                href={t('stripeAgreementLink')}
                target="_blank"
                textDecoration="underline"
              >
                {' '}
              </Link>
            ),
          }}
        />
      </Text>
      <SaveAndContinueButtons
        onSubmit={handleOnSubmit}
        personalDataState={personalDataState}
        setIsSaveAndContinueBtnClicked={setIsSaveAndContinueBtnClicked}
      />
    </>
  );
}

export default IndividualForm;
